import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend
} from "chart.js";
import {
  HiOutlineChartBar,
  HiOutlineFire,
  HiOutlineCalendarDays,
  HiOutlineTrophy,
  HiOutlineExclamationTriangle,
  HiOutlineSparkles,
  HiOutlineBolt,
  HiOutlineArrowPath,
  HiOutlineInformationCircle,
  HiChevronDown
} from "react-icons/hi2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend
);

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#CCFBF1",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
  info: "#0EA5E9"
};

const DAILY_TARGET = 40;

const MONTH_OPTIONS = [
  { value: 0, label: "January" },
  { value: 1, label: "February" },
  { value: 2, label: "March" },
  { value: 3, label: "April" },
  { value: 4, label: "May" },
  { value: 5, label: "June" },
  { value: 6, label: "July" },
  { value: 7, label: "August" },
  { value: 8, label: "September" },
  { value: 9, label: "October" },
  { value: 10, label: "November" },
  { value: 11, label: "December" }
];

function CompactMultiSelect({
  label,
  options,
  selectedValues,
  onToggle,
  onClear,
  width = 180
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const selectedLabels = options
    .filter((opt) => selectedValues.includes(opt.value))
    .map((opt) => opt.label);

  const buttonText =
    selectedLabels.length === 0
      ? label
      : selectedLabels.length <= 2
      ? selectedLabels.join(", ")
      : `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`;

  return (
    <div ref={ref} style={{ ...multiWrap, width }}>
      <button type="button" style={multiButton} onClick={() => setOpen((prev) => !prev)}>
        <span style={multiButtonText}>{buttonText}</span>
        <HiChevronDown
          style={{
            ...multiChevron,
            transform: open ? "rotate(180deg)" : "rotate(0deg)"
          }}
        />
      </button>

      {open && (
        <div style={multiMenu}>
          <div style={multiMenuInner}>
            {options.map((opt) => (
              <label key={String(opt.value)} style={multiOption}>
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt.value)}
                  onChange={() => onToggle(opt.value)}
                  style={multiCheckbox}
                />
                <span style={multiOptionText}>{opt.label}</span>
              </label>
            ))}
          </div>

          <div style={multiFooter}>
            <button type="button" style={multiClearBtn} onClick={onClear}>
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ user }) {
  const today = new Date();

  const [production, setProduction] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedYears, setSelectedYears] = useState([today.getFullYear()]);

 useEffect(() => {
  loadDashboard();
}, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setLoadError("");

      const res = await api.get(`/production/user/${user.id}`);
      setProduction(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Dashboard load failed:", err);
      setProduction([]);
      setLoadError(
        err.response?.data?.error ||
          "Unable to load dashboard data. Please refresh and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const availableYears = useMemo(() => {
    const years = [...new Set(production.map((r) => new Date(r.date).getFullYear()))]
      .filter((year) => Number.isFinite(year))
      .sort((a, b) => b - a);

    const currentYear = new Date().getFullYear();
    if (!years.includes(currentYear)) {
      years.unshift(currentYear);
    }

    return [...new Set(years)].map((year) => ({
      value: year,
      label: String(year)
    }));
  }, [production]);

  const toggleMonth = (month) => {
    setSelectedMonths((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    );
  };

  const toggleYear = (year) => {
    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  };

  const resetFilters = () => {
    setSelectedMonths([]);
    setSelectedYears([today.getFullYear()]);
  };

  const filteredRecords = useMemo(() => {
    return production.filter((r) => {
      const d = new Date(r.date);
      const monthOk =
        selectedMonths.length === 0 || selectedMonths.includes(d.getMonth());
      const yearOk =
        selectedYears.length === 0 || selectedYears.includes(d.getFullYear());

      return monthOk && yearOk;
    });
  }, [production, selectedMonths, selectedYears]);

  const todayString = today.toDateString();

  const todayRecords = useMemo(() => {
    return filteredRecords.filter(
      (r) => new Date(r.date).toDateString() === todayString
    );
  }, [filteredRecords, todayString]);

  const todayAchieved = todayRecords.reduce(
    (sum, r) => sum + Number(r.production_count || 0),
    0
  );

  const todayPercentRaw = DAILY_TARGET ? (todayAchieved / DAILY_TARGET) * 100 : 0;
  const todayPercent = Math.round(todayPercentRaw);

  const groupedByDay = useMemo(() => {
    const dayMap = {};
    filteredRecords.forEach((r) => {
      const key = new Date(r.date).toISOString().split("T")[0];
      dayMap[key] = (dayMap[key] || 0) + Number(r.production_count || 0);
    });
    return dayMap;
  }, [filteredRecords]);

  const uniqueWorkedDays = Object.keys(groupedByDay);
  const totalEntries = filteredRecords.length;

  const totalAchieved = filteredRecords.reduce(
    (sum, r) => sum + Number(r.production_count || 0),
    0
  );

  const totalTarget = uniqueWorkedDays.length * DAILY_TARGET;

  const filteredPercent = totalTarget
    ? ((totalAchieved / totalTarget) * 100).toFixed(1)
    : "0.0";

  const bestDay = useMemo(() => {
    const entries = Object.entries(groupedByDay);
    if (!entries.length) return null;
    return entries.reduce((best, curr) => (curr[1] > best[1] ? curr : best));
  }, [groupedByDay]);

  const worstDay = useMemo(() => {
    const entries = Object.entries(groupedByDay);
    if (!entries.length) return null;
    return entries.reduce((worst, curr) => (curr[1] < worst[1] ? curr : worst));
  }, [groupedByDay]);

  const bestDayPercent = bestDay ? ((bestDay[1] / DAILY_TARGET) * 100).toFixed(1) : "0.0";
  const worstDayPercent = worstDay ? ((worstDay[1] / DAILY_TARGET) * 100).toFixed(1) : "0.0";

  const missedTargetDays = useMemo(() => {
    return Object.values(groupedByDay).filter((v) => v < DAILY_TARGET).length;
  }, [groupedByDay]);

  const streak = useMemo(() => {
    let count = 0;
    for (let i = 0; i < 31; i++) {
      const d = new Date();
      d.setDate(today.getDate() - i);
      const key = d.toISOString().split("T")[0];

      if ((groupedByDay[key] || 0) >= DAILY_TARGET) {
        count += 1;
      } else {
        break;
      }
    }
    return count;
  }, [groupedByDay, today]);

  const quarterMap = useMemo(() => {
    const map = {};

    filteredRecords.forEach((r) => {
      const d = new Date(r.date);
      const year = d.getFullYear();
      const quarter = Math.floor(d.getMonth() / 3) + 1;
      const key = `${year}-Q${quarter}`;

      if (!map[key]) {
        map[key] = {
          achieved: 0,
          workedDays: new Set(),
          year,
          quarter
        };
      }

      map[key].achieved += Number(r.production_count || 0);
      map[key].workedDays.add(d.toISOString().split("T")[0]);
    });

    return map;
  }, [filteredRecords]);

  const latestQuarter = useMemo(() => {
    const rows = Object.values(quarterMap);
    if (!rows.length) return null;

    rows.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.quarter - a.quarter;
    });

    return rows[0];
  }, [quarterMap]);

  const quarterlyPercent = latestQuarter
    ? (
        (latestQuarter.achieved / (latestQuarter.workedDays.size * DAILY_TARGET || 1)) *
        100
      ).toFixed(1)
    : "0.0";

  const quarterlyLabel = latestQuarter
    ? `Q${latestQuarter.quarter} ${latestQuarter.year}`
    : "No quarter data";

  const filteredDates = useMemo(() => {
    return filteredRecords
      .map((r) => new Date(r.date))
      .sort((a, b) => a - b);
  }, [filteredRecords]);

  const weeklyTrendRows = useMemo(() => {
    const endDate = filteredDates.length
      ? new Date(filteredDates[filteredDates.length - 1])
      : new Date();

    const rows = [];

    for (let i = 6; i >= 0; i--) {
      const d = new Date(endDate);
      d.setDate(endDate.getDate() - i);

      const key = d.toISOString().split("T")[0];
      const achieved = filteredRecords
        .filter((r) => new Date(r.date).toISOString().split("T")[0] === key)
        .reduce((sum, r) => sum + Number(r.production_count || 0), 0);

      const percent = Number(((achieved / DAILY_TARGET) * 100).toFixed(1));

      rows.push({
        label: d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
        percent,
        targetPercent: 100
      });
    }

    return rows;
  }, [filteredDates, filteredRecords]);

  const weeklyTrendData = {
    labels: weeklyTrendRows.map((d) => d.label),
    datasets: [
      {
        label: "Productivity %",
        data: weeklyTrendRows.map((d) => d.percent),
        borderColor: theme.primary,
        backgroundColor: "rgba(20,184,166,0.10)",
        fill: false,
        tension: 0.35,
        pointRadius: 3,
        pointHoverRadius: 4,
        pointBackgroundColor: theme.primary,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2
      },
      {
        label: "Target %",
        data: weeklyTrendRows.map((d) => d.targetPercent),
        borderColor: theme.primaryLight,
        backgroundColor: "rgba(45,212,191,0.08)",
        fill: false,
        tension: 0.35,
        pointRadius: 2,
        pointHoverRadius: 3,
        pointBackgroundColor: theme.primaryLight,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2
      }
    ]
  };

  const taskMap = useMemo(() => {
    const map = {};
    filteredRecords.forEach((r) => {
      const task = r.task || "Unknown";
      if (!map[task]) {
        map[task] = {
          count: 0,
          hours: 0
        };
      }
      map[task].count += Number(r.production_count || 0);
      map[task].hours += Number(r.hours_contributed || 0);
    });
    return map;
  }, [filteredRecords]);

  const taskLabels = Object.keys(taskMap);
  const taskValues = taskLabels.map((label) => taskMap[label].count);

  const topTask = taskLabels.length
    ? taskLabels.reduce((a, b) => (taskMap[a].count > taskMap[b].count ? a : b))
    : "-";

  const taskChartData = {
    labels: taskLabels,
    datasets: [
      {
        data: taskValues,
        backgroundColor: [
          theme.primary,
          theme.primaryLight,
          theme.accent,
          theme.info,
          theme.warning,
          "#34D399",
          "#60A5FA",
          "#F472B6"
        ],
        borderColor: "#ffffff",
        borderWidth: 2
      }
    ]
  };

  const taskSummaryRows = taskLabels.map((label) => {
    const count = taskMap[label].count;
    const hours = taskMap[label].hours;
    const share = totalAchieved ? ((count / totalAchieved) * 100).toFixed(1) : "0.0";

    return {
      label,
      count,
      hours,
      share
    };
  });

  const monthPerformance = useMemo(() => {
    const dateMap = {};

    filteredRecords.forEach((r) => {
      const dateObj = new Date(r.date);
      const isoDate = dateObj.toISOString().split("T")[0];

      if (!dateMap[isoDate]) {
        dateMap[isoDate] = {
          dateObj,
          totalCount: 0
        };
      }

      dateMap[isoDate].totalCount += Number(r.production_count || 0);
    });

    const sortedRows = Object.entries(dateMap)
      .map(([isoDate, value]) => ({
        isoDate,
        dateObj: value.dateObj,
        label: value.dateObj.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short"
        }),
        percent: Number(((value.totalCount / DAILY_TARGET) * 100).toFixed(1))
      }))
      .sort((a, b) => a.dateObj - b.dateObj);

    return {
      labels: sortedRows.map((row) => row.label),
      values: sortedRows.map((row) => row.percent)
    };
  }, [filteredRecords]);

  const monthPerformanceMax = useMemo(() => {
    if (!monthPerformance.values.length) return 120;
    const highest = Math.max(...monthPerformance.values);
    if (highest <= 100) return 100;
    return Math.ceil(highest / 20) * 20;
  }, [monthPerformance.values]);

  const monthPerformanceData = {
    labels: monthPerformance.labels,
    datasets: [
      {
        label: "Productivity %",
        data: monthPerformance.values,
        backgroundColor: "rgba(15,118,110,0.78)",
        borderRadius: 8,
        maxBarThickness: 28
      }
    ]
  };

  const todayStatus = (() => {
    if (todayPercentRaw >= 100) return { text: "On Target", color: theme.success };
    if (todayPercentRaw >= 70) return { text: "In Progress", color: theme.warning };
    return { text: "Needs Attention", color: theme.danger };
  })();

  const insightMessage = (() => {
    if (loadError) {
      return "Dashboard data could not be loaded. Please refresh and verify login/session.";
    }
    if (!filteredRecords.length) {
      return "No entries found for the selected month and year.";
    }
    if (todayAchieved === 0) {
      return "No production logged for today under the current filters.";
    }
    if (todayPercentRaw >= 100) {
      return "Excellent — you have already met today’s target.";
    }
    if (streak >= 3) {
      return `Strong consistency. You have hit target for ${streak} consecutive day(s).`;
    }
    if (topTask !== "-") {
      return `Your top-performing task in the filtered view is "${topTask}".`;
    }
    return "You are building steady momentum. Focus on closing the gap to today’s target.";
  })();

  const progressCircle = {
    background: `conic-gradient(${theme.primary} ${Math.min(todayPercent, 100) * 3.6}deg, #D1FAE5 0deg)`
  };

  if (loading) {
    return (
      <div style={loadingWrap}>
        <div style={loadingCard}>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={hero}>
        <div>
          <h2 style={heroTitle}>Welcome back, {user?.name || "User"}</h2>
          <p style={heroText}>
            Filter by month and year to analyze dashboard performance.
          </p>
        </div>

        <div style={heroRight}>
          <div style={controlsRow}>
            <CompactMultiSelect
              label="Months"
              options={MONTH_OPTIONS}
              selectedValues={selectedMonths}
              onToggle={toggleMonth}
              onClear={() => setSelectedMonths([])}
              width={180}
            />

            <CompactMultiSelect
              label="Years"
              options={availableYears}
              selectedValues={selectedYears}
              onToggle={toggleYear}
              onClear={() => setSelectedYears([])}
              width={140}
            />

            <button type="button" onClick={resetFilters} style={resetBtn}>
              Reset
            </button>

            <button type="button" onClick={loadDashboard} style={refreshBtn}>
              <HiOutlineArrowPath style={{ fontSize: 15 }} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {loadError && (
        <div style={errorBanner}>
          <HiOutlineInformationCircle style={{ fontSize: 18, flexShrink: 0 }} />
          <span>{loadError}</span>
        </div>
      )}

      <div style={topGrid}>
        <div style={primaryCard}>
          <div style={primaryTop}>
            <div>
              <p style={cardLabelWhite}>Today’s Progress</p>
              <h3 style={primaryValue}>{todayPercent}%</h3>
              <p style={primarySub}>
                {todayAchieved} achieved out of {DAILY_TARGET}
              </p>
            </div>

            <div style={{ ...progressCircleWrap, ...progressCircle }}>
              <div style={progressCircleInner}>
                <span style={progressCircleText}>{todayPercent}%</span>
              </div>
            </div>
          </div>

          <div style={progressRow}>
            <div style={progressTrack}>
              <div
                style={{
                  ...progressFill,
                  width: `${Math.min(todayPercent, 100)}%`
                }}
              />
            </div>
            <span style={{ ...statusPill, background: todayStatus.color }}>
              {todayStatus.text}
            </span>
          </div>
        </div>

        <div style={statCard}>
          <div style={iconBubble}>
            <HiOutlineChartBar />
          </div>
          <p style={cardLabel}>Filtered Productivity</p>
          <h3 style={statValue}>{filteredPercent}%</h3>
          <p style={cardMuted}>
            {totalAchieved} / {totalTarget || 0}
          </p>
        </div>

        <div style={statCard}>
          <div style={iconBubble}>
            <HiOutlineCalendarDays />
          </div>
          <p style={cardLabel}>Days Worked</p>
          <h3 style={statValue}>{uniqueWorkedDays.length}</h3>
          <p style={cardMuted}>Filtered view</p>
        </div>

        <div style={statCard}>
          <div style={iconBubble}>
            <HiOutlineBolt />
          </div>
          <p style={cardLabel}>Quarterly Productivity</p>
          <h3 style={statValue}>{quarterlyPercent}%</h3>
          <p style={cardMuted}>{quarterlyLabel}</p>
        </div>
      </div>

      <div style={chartsGrid}>
        <div style={panel}>
          <div style={panelHeader}>
            <div>
              <h3 style={panelTitle}>Weekly Productivity Trend</h3>
              <p style={panelSubtitle}>Last 7 dates in percentage vs 100% target</p>
            </div>
          </div>

          <div style={chartAreaSmall}>
            <Line
              data={weeklyTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top",
                    labels: {
                      color: theme.textMuted,
                      font: { size: 11 }
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.dataset.label}: ${context.raw}%`
                    }
                  }
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { color: theme.textMuted, font: { size: 11 } }
                  },
                  y: {
                    beginAtZero: true,
                    max: 140,
                    ticks: {
                      color: theme.textMuted,
                      font: { size: 11 },
                      callback: (value) => `${value}%`
                    },
                    grid: { color: "#E2E8F0" }
                  }
                }
              }}
            />
          </div>
        </div>

        <div style={panel}>
          <div style={panelHeader}>
            <div>
              <h3 style={panelTitle}>Task Contribution</h3>
              <p style={panelSubtitle}>Task-wise production and hours summary</p>
            </div>
          </div>

          <div style={taskPanelWrap}>
            <div style={taskChartWrap}>
              {taskLabels.length ? (
                <Doughnut
                  data={taskChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: "68%",
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          color: theme.textMuted,
                          padding: 12,
                          boxWidth: 10,
                          font: { size: 11 }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            const label = context.label || "";
                            const count = context.raw || 0;
                            const hours = taskMap[label]?.hours || 0;
                            return `${label}: ${count} count | Hours: ${hours || "-"}`;
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div style={emptyState}>No task data yet</div>
              )}
            </div>

            <div style={taskSummaryBox}>
              {taskSummaryRows.length === 0 ? (
                <div style={taskEmptyText}>No task summary available</div>
              ) : (
                taskSummaryRows.map((row) => (
                  <div key={row.label} style={taskRow}>
                    <div style={taskRowLeft}>
                      <span style={taskTaskName}>{row.label}</span>
                      <span style={taskTaskMetaSingle}>
                        Count: {row.count} | Share: {row.share}% | Hours: {row.hours > 0 ? row.hours : "-"}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={bottomGrid}>
        <div style={panel}>
          <div style={panelHeader}>
            <div>
              <h3 style={panelTitle}>Monthly Performance</h3>
              <p style={panelSubtitle}>Daily productivity % in filtered view</p>
            </div>
          </div>

          <div style={chartAreaLarge}>
            {monthPerformance.labels.length ? (
              <Bar
                data={monthPerformanceData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (context) => `Productivity: ${context.raw}%`
                      }
                    }
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: { color: theme.textMuted, font: { size: 11 } }
                    },
                    y: {
                      beginAtZero: true,
                      max: monthPerformanceMax,
                      ticks: {
                        color: theme.textMuted,
                        font: { size: 11 },
                        callback: (value) => `${value}%`
                      },
                      grid: { color: "#E2E8F0" }
                    }
                  }
                }}
              />
            ) : (
              <div style={emptyState}>No monthly data available</div>
            )}
          </div>
        </div>

        <div style={summaryColumn}>
          <div style={miniGrid}>
            <div style={miniCard}>
              <div style={{ ...miniIcon, background: "#ECFDF5", color: theme.success }}>
                <HiOutlineTrophy />
              </div>
              <p style={cardLabel}>Best Day</p>
              <h4 style={miniValue}>{bestDayPercent}%</h4>
              <p style={cardMutedSmall}>
                {bestDay ? new Date(bestDay[0]).toLocaleDateString() : "No data"}
              </p>
            </div>

            <div style={miniCard}>
              <div style={{ ...miniIcon, background: "#FEF3C7", color: theme.warning }}>
                <HiOutlineExclamationTriangle />
              </div>
              <p style={cardLabel}>Missed Target Days</p>
              <h4 style={miniValue}>{missedTargetDays}</h4>
              <p style={cardMutedSmall}>Filtered view</p>
            </div>

            <div style={miniCard}>
              <div style={{ ...miniIcon, background: "#ECFEFF", color: theme.primary }}>
                <HiOutlineFire />
              </div>
              <p style={cardLabel}>Current Streak</p>
              <h4 style={miniValue}>{streak}</h4>
              <p style={cardMutedSmall}>Target-hit days</p>
            </div>

            <div style={miniCard}>
              <div style={{ ...miniIcon, background: "#FEE2E2", color: theme.danger }}>
                <HiOutlineChartBar />
              </div>
              <p style={cardLabel}>Worst Day</p>
              <h4 style={miniValue}>{worstDayPercent}%</h4>
              <p style={cardMutedSmall}>
                {worstDay ? new Date(worstDay[0]).toLocaleDateString() : "No data"}
              </p>
            </div>
          </div>

          <div style={insightPanel}>
            <div style={insightHeader}>
              <HiOutlineSparkles style={{ fontSize: 18, color: theme.primary }} />
              <h3 style={insightTitle}>Smart Insight</h3>
            </div>

            <p style={insightText}>{insightMessage}</p>

            <div style={insightFooter}>
              <span style={insightChip}>Top Task: {topTask}</span>
              <span style={insightChipSecondary}>Entries: {totalEntries}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  fontFamily: "sans-serif",
  color: theme.text,
  padding: "12px 6px",
  overflow: "visible"
};

const loadingWrap = {
  padding: 20
};

const loadingCard = {
  background: theme.surface,
  borderRadius: 16,
  padding: 24,
  boxShadow: "0 8px 24px rgba(15,23,42,0.06)"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  marginBottom: 18,
  flexWrap: "wrap"
};

const heroTitle = {
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  color: theme.text
};

const heroText = {
  margin: "6px 0 0 0",
  color: theme.textMuted,
  fontSize: 13
};

const heroRight = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
  position: "relative",
  zIndex: 50
};

const controlsRow = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap"
};

const multiWrap = {
  position: "relative",
  zIndex: 60
};

const multiButton = {
  width: "100%",
  height: 38,
  borderRadius: 12,
  border: "1px solid #D1D5DB",
  background: "#fff",
  color: theme.text,
  padding: "0 34px 0 12px",
  fontSize: 13,
  fontWeight: 600,
  textAlign: "left",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden"
};

const multiButtonText = {
  display: "block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const multiChevron = {
  position: "absolute",
  right: 10,
  top: 10,
  fontSize: 16,
  color: theme.textMuted,
  transition: "transform 0.2s ease"
};

const multiMenu = {
  position: "absolute",
  top: "calc(100% + 8px)",
  left: 0,
  width: "100%",
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 14,
  boxShadow: "0 14px 32px rgba(15,23,42,0.10)",
  overflow: "hidden",
  zIndex: 9999
};

const multiMenuInner = {
  maxHeight: 220,
  overflowY: "auto",
  padding: 8
};

const multiOption = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 10px",
  borderRadius: 10,
  cursor: "pointer"
};

const multiCheckbox = {
  accentColor: "#0F766E",
  cursor: "pointer"
};

const multiOptionText = {
  fontSize: 13,
  color: theme.text
};

const multiFooter = {
  borderTop: "1px solid #F1F5F9",
  padding: 8,
  display: "flex",
  justifyContent: "flex-end"
};

const multiClearBtn = {
  border: "none",
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  borderRadius: 10,
  padding: "8px 12px",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer"
};

const resetBtn = {
  height: 38,
  padding: "0 16px",
  borderRadius: 12,
  border: "none",
  background: "#E2E8F0",
  color: theme.text,
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer"
};

const refreshBtn = {
  height: 38,
  padding: "0 16px",
  borderRadius: 12,
  border: "none",
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#fff",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer"
};

const errorBanner = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 14,
  background: "#FEF2F2",
  color: theme.danger,
  border: "1px solid #FECACA",
  borderRadius: 14,
  padding: "12px 14px",
  fontSize: 13,
  fontWeight: 600
};

const topGrid = {
  display: "grid",
  gridTemplateColumns: "1.35fr 1fr 1fr 1fr",
  gap: 12,
  marginBottom: 14
};

const chartsGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  alignItems: "stretch",
  marginBottom: 12
};

const bottomGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  alignItems: "stretch"
};

const summaryColumn = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  height: "100%"
};

const primaryCard = {
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#ffffff",
  borderRadius: 18,
  padding: 16,
  boxShadow: "0 12px 30px rgba(15,118,110,0.20)"
};

const primaryTop = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16
};

const primaryValue = {
  margin: "6px 0",
  fontSize: 28,
  fontWeight: 800
};

const primarySub = {
  margin: 0,
  fontSize: 12,
  opacity: 0.92
};

const cardLabelWhite = {
  margin: 0,
  fontSize: 12,
  color: "rgba(255,255,255,0.86)",
  fontWeight: 600
};

const progressRow = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 12
};

const progressTrack = {
  flex: 1,
  height: 8,
  background: "rgba(255,255,255,0.25)",
  borderRadius: 999,
  overflow: "hidden"
};

const progressFill = {
  height: "100%",
  background: "#ffffff",
  borderRadius: 999
};

const statusPill = {
  color: "#ffffff",
  padding: "5px 9px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700
};

const progressCircleWrap = {
  width: 80,
  height: 80,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0
};

const progressCircleInner = {
  width: 58,
  height: 58,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.94)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const progressCircleText = {
  color: theme.primaryDark,
  fontWeight: 800,
  fontSize: 14
};

const statCard = {
  background: theme.surface,
  borderRadius: 16,
  padding: 14,
  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  border: "1px solid #F1F5F9",
  minHeight: 118
};

const iconBubble = {
  width: 34,
  height: 34,
  borderRadius: 10,
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  marginBottom: 10
};

const statValue = {
  margin: "4px 0",
  fontSize: 22,
  fontWeight: 800,
  color: theme.text
};

const cardLabel = {
  margin: 0,
  fontSize: 12,
  color: theme.textMuted,
  fontWeight: 600
};

const cardMuted = {
  margin: "4px 0 0 0",
  color: theme.textMuted,
  fontSize: 12
};

const cardMutedSmall = {
  margin: "4px 0 0 0",
  color: theme.textMuted,
  fontSize: 11
};

const panel = {
  background: theme.surface,
  borderRadius: 18,
  padding: 14,
  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  border: "1px solid #F1F5F9",
  height: "100%",
  boxSizing: "border-box"
};

const panelHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 10
};

const panelTitle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: theme.text
};

const panelSubtitle = {
  margin: "4px 0 0 0",
  fontSize: 12,
  color: theme.textMuted
};

const chartAreaSmall = {
  height: 250
};

const chartAreaLarge = {
  height: 320
};

const taskPanelWrap = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  alignItems: "start"
};

const taskChartWrap = {
  height: 260
};

const taskSummaryBox = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  maxHeight: 260,
  overflowY: "auto",
  paddingRight: 2
};

const taskRow = {
  padding: "10px 12px",
  borderRadius: 12,
  background: "#F8FAFC",
  border: "1px solid #E2E8F0"
};

const taskRowLeft = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  minWidth: 0
};

const taskTaskName = {
  fontSize: 13,
  fontWeight: 700,
  color: theme.text,
  wordBreak: "break-word"
};

const taskTaskMetaSingle = {
  fontSize: 11,
  color: theme.textMuted,
  lineHeight: 1.5,
  wordBreak: "break-word"
};

const taskEmptyText = {
  color: theme.textMuted,
  fontSize: 12
};

const miniGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12
};

const miniCard = {
  background: theme.surface,
  borderRadius: 16,
  padding: 14,
  boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
  border: "1px solid #F1F5F9",
  minHeight: 142,
  boxSizing: "border-box"
};

const miniIcon = {
  width: 32,
  height: 32,
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  marginBottom: 8
};

const miniValue = {
  margin: "4px 0",
  fontSize: 20,
  fontWeight: 800,
  color: theme.text
};

const insightPanel = {
  background: "linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)",
  border: `1px solid ${theme.border}`,
  borderRadius: 18,
  padding: 16,
  boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  minHeight: 140,
  boxSizing: "border-box"
};

const insightHeader = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10
};

const insightTitle = {
  margin: 0,
  fontSize: 15,
  color: theme.text
};

const insightText = {
  margin: 0,
  color: theme.text,
  fontSize: 13,
  lineHeight: 1.6
};

const insightFooter = {
  marginTop: 12,
  display: "flex",
  gap: 8,
  flexWrap: "wrap"
};

const insightChip = {
  display: "inline-block",
  padding: "7px 10px",
  borderRadius: 999,
  background: "#CCFBF1",
  color: theme.primaryDark,
  fontSize: 11,
  fontWeight: 700
};

const insightChipSecondary = {
  display: "inline-block",
  padding: "7px 10px",
  borderRadius: 999,
  background: "#E2E8F0",
  color: theme.text,
  fontSize: 11,
  fontWeight: 700
};

const emptyState = {
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.textMuted,
  fontSize: 13
};

export default Dashboard;